import { Component, DestroyRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CompanyConfiguration } from '@app-config/models/company-configuration';
import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import { serverMessagesMapping } from '@auth/pages/forgot-password/server-message-mapping/server-message-mapping';
import * as restorePaswordActions from '@auth/store/actions/restore-password.actions';
import { RestorePasswordEffects } from '@auth/store/effects/restore-password.effects';
import { AuthStoreService } from '@auth/store/services/auth-store.service';
import { fadeInScaleAnimation } from '@core-animations/element-animations';
import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { ServerMessageService } from '@core-services/server-message.service';
import { ToastService } from '@core-services/toast.service';
import { ApiError } from '@error/models/api-error';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogoComponent } from '../../../../core-modules/layout/logo/logo.component';
import { RpcInputComponent } from '../../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { RpcButtonComponent } from '../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { AdditionalFooterComponent } from '../../../../core-modules/layout/additional-footer/additional-footer.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['../../auth-base/auth-base.component.scss', './forgot-password.component.scss'],
    animations: [fadeInScaleAnimation],
    imports: [LogoComponent, FormsModule, ReactiveFormsModule, RpcInputComponent, RpcButtonComponent, AdditionalFooterComponent, TranslateModule]
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
    public forgotPasswordForm: UntypedFormGroup;
    public userNameControlOptions: RpcInputOptions;
    public companyConfiguration: CompanyConfiguration;

    public readonly RpcRoute = RpcRoute;

    constructor(
        private readonly authStoreService: AuthStoreService,
        private readonly restorePasswordEffects: RestorePasswordEffects,
        private readonly configurationService: AppConfigurationService,
        private readonly routeService: RouteService,
        private readonly toaster: ToastService,
        private readonly serverMessageService: ServerMessageService,
        private readonly renderer: Renderer2,
        private readonly destroyRef: DestroyRef
    ) {
        configurationService.configuration = { layout: { spinner: { hidden: true } } };

        this.serverMessageService.loadServerMessagesMapping(serverMessagesMapping);
    }

    public ngOnInit(): void {
        this.renderer.addClass(document.body, 'auth-page');

        this.forgotPasswordForm = new UntypedFormGroup({});

        this.userNameControlOptions = {
            formGroup: this.forgotPasswordForm,
            controlName: 'userName',
            validatorsMap: [
                {
                    message: 'FORGOT_PASSWORD.VALIDATION_ERRORS.EMAIL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                },
                {
                    message: 'FORGOT_PASSWORD.VALIDATION_ERRORS.EMAIL_VALID',
                    showError: (control: UntypedFormControl) => control.hasError('email'),
                    validator: Validators.email
                }
            ]
        };

        this.restorePasswordEffects.sendPasswordRestoreLink$
            .pipe(
                ofType(restorePaswordActions.sendRestorePasswordLinkSuccessful),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(
                () => {
                    this.routeService.navigate(RpcRoute.Login)
                        .then(() => this.toaster.showClientSuccess('FORGOT_PASSWORD.MESSAGES.LINK_SENT_SUCCESSFULLY'))
                        .catch(() => { });
                });

        this.restorePasswordEffects.sendPasswordRestoreLink$
            .pipe(
                ofType(restorePaswordActions.sendRestorePasswordLinkFailed),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(
                (error: ApiError) => this.showErrorMessage(error));

        this.configurationService.configuration$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(configuration => this.companyConfiguration = configuration.company);
    }

    public ngOnDestroy(): void {
        this.renderer.removeClass(document.body, 'auth-page');
    }

    public sendEmail(): void {
        const userName = this.forgotPasswordForm.get('userName').value as string;

        this.authStoreService.sendRestorePasswordLink(userName);
    }

    private showErrorMessage(error: ApiError): void {
        this.toaster.showServerError(error);
    }
}